import React, { useState } from 'react';
import { ProductsManagementProps, Product } from '../../../types';
import { ProductTable, ProductForm, BatchUploadModal, ProductGrid } from '../molecules';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon';
import { ViewToggle } from '../atoms';
import './ProductsManagement.css';

const ProductsManagement: React.FC<ProductsManagementProps> = ({
  products,
  collections,
  loading = false,
  onCreateProduct,
  onUpdateProduct,
  onDeleteProduct,
  onDuplicateProduct,
  onRefresh,
  selectedCollectionId = 'all',
  onCollectionFilterChange
}) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | undefined>(undefined);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Product | null>(null);
  const [showBatchUpload, setShowBatchUpload] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  const handleAddNew = () => {
    setEditingProduct(undefined);
    setIsFormVisible(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsFormVisible(true);
  };

  const handleDelete = (product: Product) => {
    setShowDeleteConfirm(product);
  };

  const handleDuplicate = (product: Product) => {
    onDuplicateProduct(product);
  };

  const handleFormSubmit = (productData: Partial<Product>) => {
    if (editingProduct) {
      onUpdateProduct({ ...editingProduct, ...productData } as Product);
    } else {
      onCreateProduct(productData);
    }
    setIsFormVisible(false);
    setEditingProduct(undefined);
  };

  const handleFormCancel = () => {
    setIsFormVisible(false);
    setEditingProduct(undefined);
  };

  const handleConfirmDelete = () => {
    if (showDeleteConfirm) {
      onDeleteProduct(showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  };
  
  const handleBatchUpload = (products: Partial<Product>[]) => {
    console.log('🔍 ProductsManagement - Starting batch upload with products:', products);
    
    // Process each product in the batch
    products.forEach((product, index) => {
      console.log(`🔍 ProductsManagement - Processing product ${index + 1}:`, product);
      
      // Fix flavor prices if they're undefined (final safety check, but preserve 0 values)
      if (product.flavors && Array.isArray(product.flavors)) {
        product.flavors = product.flavors.map(flavor => ({
          ...flavor,
          price: flavor.price === undefined || flavor.price === null ? 0 : flavor.price
        }));
      }
      
      // Check for undefined values before sending to createProduct
      Object.entries(product).forEach(([key, value]) => {
        if (value === undefined) {
          console.error(`❌ UNDEFINED VALUE FOUND in ProductsManagement before createProduct: ${key} = ${value}`);
        }
      });
      
      onCreateProduct(product);
    });
    
    setShowBatchUpload(false);
  };

  return (
    <div className="products-management">
      {isFormVisible ? (
        <ProductForm
          product={editingProduct}
          collections={collections}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          loading={loading}
        />
      ) : (
        <>
          <div className="products-header">
            <div className="products-title">
              <h2>Products</h2>
              <span className="products-count">{products.length} products</span>
              <ViewToggle activeView={viewMode} onChange={setViewMode} />
            </div>
            <div className="products-filters">
              <select 
                value={selectedCollectionId} 
                onChange={(e) => onCollectionFilterChange?.(e.target.value)}
                className="collection-filter"
              >
                <option value="all">All Collections</option>
                {collections.map(collection => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="products-actions">
              {onRefresh && (
                <Button 
                  variant="secondary" 
                  onClick={onRefresh}
                  className="refresh-button"
                >
                  <Icon type="refresh" size="small" />
                  Refresh
                </Button>
              )}
              <Button 
                variant="secondary" 
                onClick={() => setShowBatchUpload(true)}
                className="batch-upload-button"
              >
                <Icon type="upload" size="small" />
                Batch Upload
              </Button>
              <Button 
                variant="primary" 
                onClick={handleAddNew}
                className="add-product-button"
              >
                <Icon type="add" size="small" />
                Add New Product
              </Button>
            </div>
          </div>
          
          {viewMode === 'table' ? (
            <ProductTable
              products={products}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              loading={loading}
            />
          ) : (
            <ProductGrid
              products={products}
              onProductClick={handleEdit}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              loading={loading}
            />
          )}
        </>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="delete-modal-backdrop">
          <div className="delete-modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>?</p>
            <p className="delete-warning">This action cannot be undone.</p>
            <div className="delete-modal-actions">
              <Button 
                variant="secondary" 
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleConfirmDelete}
                className="delete-confirm-button"
              >
                Delete Product
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Batch upload modal */}
      {showBatchUpload && (
        <BatchUploadModal
          onClose={() => setShowBatchUpload(false)}
          onUpload={handleBatchUpload}
          collections={collections}
          loading={loading}
        />
      )}
    </div>
  );
};

export default ProductsManagement;
